import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { useReveal } from "@/hooks/use-reveal";
import { DevisWizard } from "@/components/devis-wizard";
import { PageHero } from "@/components/page-hero";
import heroImg from "@/assets/dj-mariage-ceremony.jpg";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
export const Route = createFileRoute("/contactez-nous")({
  validateSearch: z.object({
    sujet: z.string().optional(),
  }),

  head: () => ({
    meta: [
      { title: "Contact : devis DJ mariage gratuit | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Contactez DJ Val pour un devis gratuit personnalisé sous 24h. DJ mariage et événements en Provence et Vaucluse. Téléphone, e-mail, formulaire.")
      },
      { property: "og:title", content: "Contact — DJ Val" },
      { property: "og:description", content: metaDesc("Devis gratuit personnalisé sous 24h.") },
      { property: "og:url", content: "https://dj-val.fr/contactez-nous" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/contactez-nous" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Combien de temps à l'avance réserver un DJ mariage ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Idéalement entre 6 et 12 mois avant la date. Les mariages en haute saison (mai à septembre) dans le Vaucluse sont souvent réservés un an à l'avance.",
              },
            },
            {
              "@type": "Question",
              name: "Intervenez-vous en dehors du Vaucluse ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Oui, DJ Val se déplace dans un rayon d'environ 100 km autour de Cavaillon : Avignon, Aix-en-Provence, Arles, Nîmes, Orange, Uzès, Salon-de-Provence, Carpentras, Apt.",
              },
            },
            {
              "@type": "Question",
              name: "Le matériel de sonorisation et d'éclairage est-il inclus ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Oui, chaque prestation inclut la sonorisation professionnelle et un kit d'éclairage LED. Pour les très grands lieux, du matériel additionnel peut être ajouté.",
              },
            },
            {
              "@type": "Question",
              name: "Sonorisez-vous la cérémonie ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Oui. Micros HF discrets, retours pour les officiants et diffusion musicale haute fidélité, en intérieur comme en extérieur.",
              },
            },
            {
              "@type": "Question",
              name: "Que se passe-t-il en cas de panne technique ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Tout notre matériel est doublé : table de mixage, enceintes, micros et ordinateurs de secours. Garantie d'une soirée sans coupure.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const FAQS = [
  {
    q: "Combien de temps à l'avance réserver un DJ mariage ?",
    a: "Idéalement entre 6 et 12 mois avant la date. Les mariages en haute saison (mai à septembre) dans le Vaucluse, Avignon ou Aix-en-Provence sont souvent réservés un an à l'avance.",
  },
  {
    q: "Intervenez-vous en dehors du Vaucluse ?",
    a: "Oui, DJ Val se déplace dans un rayon d'environ 100 km autour de Cavaillon : Avignon, Aix-en-Provence, Arles, Nîmes, Orange, Uzès, Salon-de-Provence, Carpentras, Apt, ainsi que tout le Luberon et les Alpilles.",
  },
  {
    q: "Le matériel sonorisation et éclairage est-il inclus ?",
    a: "Oui, chaque prestation inclut la sonorisation professionnelle adaptée à votre salle et un kit d'éclairage LED.",
  },
  {
    q: "Sonorisez-vous la cérémonie ?",
    a: "Oui. Micros HF discrets, retours pour les officiants et diffusion musicale haute fidélité, en intérieur comme en extérieur.",
  },
  {
    q: "Que se passe-t-il en cas de panne technique ?",
    a: "Tout notre matériel est doublé : table de mixage, enceintes, micros et ordinateurs de secours. Garantie d'une soirée sans coupure.",
  },
];

function Page() {
  useReveal();
  const { sujet } = Route.useSearch();
  const [open, setOpen] = useState<number | null>(0);




  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={heroImg}
        eyebrow="Contact"
        title={<>Parlons de votre <span className="italic">événement</span></>}
        subtitle="Devis personnalisé gratuit sous 24 h. Échange téléphonique sans engagement."
        primary={{ label: "07 67 90 86 67", href: "tel:0767908667" }}
        secondary={{ label: "Email direct", href: "mailto:info.djval@gmail.com" }}
        height="compact"
      />
      <ReviewsProof />

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16">
          <div className="sr" data-reveal>
            <p className="eyebrow text-foreground">Mes coordonnées</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4 leading-tight">
              Trois façons simples<span className="block italic mt-1">de me joindre</span>
            </h2>
            <span className="gold-rule !w-12 my-8" />
            <p className="text-base text-muted-foreground leading-loose font-light">
              Décrivez-nous votre projet — date, lieu, nombre d'invités, ambiance souhaitée. Vous recevrez un devis personnalisé sous 24 h.
            </p>


            <div className="mt-10 space-y-5">
              <a
                href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "contact_page" })}
                className="flex items-center gap-4 group"
              >
                <span className="w-12 h-12 border border-foreground/10 flex items-center justify-center text-foreground group-hover:bg-[var(--gold)] group-hover:text-[var(--ink)] transition-colors">
                  ☎
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Téléphone</p>
                  <p className="text-lg text-foreground font-serif-display italic">07 67 90 86 67</p>
                </div>
              </a>
              <a href="mailto:info.djval@gmail.com" onClick={() => trackEvent("contact_click", { contact_method: "email", cta_location: "contact_page" })} className="flex items-center gap-4 group">
                <span className="w-12 h-12 border border-foreground/10 flex items-center justify-center text-foreground group-hover:bg-[var(--gold)] group-hover:text-[var(--ink)] transition-colors">
                  ✉
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">E-mail</p>
                  <p className="text-lg text-foreground font-serif-display italic">info.djval@gmail.com</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 border border-foreground/10 flex items-center justify-center text-foreground">
                  ◉
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Zone</p>
                  <p className="text-lg text-foreground font-serif-display italic">
                    Cavaillon · Vaucluse · ≈ 100 km
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="sr" data-reveal style={{ transitionDelay: "120ms" }}>
            <DevisWizard initialSubject={sujet ?? ""} />
          </div>


        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-card border-t border-foreground/10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 sr" data-reveal>
            <p className="eyebrow text-foreground">Questions fréquentes</p>
            <h2 className="font-serif-display text-4xl text-foreground mt-4">FAQ</h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div
                key={i}
                className="border border-foreground/10 sr"
                data-reveal
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full text-left flex justify-between items-center gap-4 px-6 py-5 hover:bg-background transition-colors"
                >
                  <span className="font-serif-display text-lg text-foreground">{f.q}</span>
                  <span className="text-foreground text-xl">
                    {open === i ? "−" : "+"}
                  </span>
                </button>
                {open === i && (
                  <div className="px-6 pb-6 text-sm text-muted-foreground leading-loose font-light">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE MAPS */}
      <section className="py-20 px-6 border-t border-foreground/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sr" data-reveal>
            <p className="eyebrow text-foreground">Notre base</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
              Cavaillon · <span className="italic">Vaucluse</span>
            </h2>
            <span className="gold-rule mx-auto !w-12 my-6" />
            <p className="text-sm text-muted-foreground font-light max-w-xl mx-auto">
              106 Grand Rue, 84300 Cavaillon — au cœur du Vaucluse, à 25 min d'Avignon et 30 min de Gordes.
              Retrait du matériel sur place, sur rendez-vous.
            </p>
          </div>
          <div className="aspect-[16/8] overflow-hidden border border-foreground/10 sr" data-reveal style={{ transitionDelay: "100ms" }}>
            <iframe
              title="DJ Val — 106 Grand Rue, 84300 Cavaillon"
              src="https://www.google.com/maps?q=106+Grand+Rue,+84300+Cavaillon&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </div>
  );
}
